import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { LANGS } from "@/i18n";

/**
 * <LanguageSwitcher /> — dropdown in the sidebar footer. Compact, brand-styled.
 * Persists the choice to localStorage via i18next-browser-languagedetector.
 */
export default function LanguageSwitcher({ compact = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const current = LANGS.find((l) => l.code === (i18n.language || "en").slice(0, 2)) || LANGS[0];

  const pick = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        data-testid="lang-current"
        className={`flex items-center gap-2 rounded-md w-full text-left transition ${
          compact
            ? "px-3 py-2 text-xs text-slate-200 hover:bg-white/10"
            : "px-3 py-2 text-sm text-[#475569] dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
        }`}
      >
        <Globe className="w-4 h-4" />
        <span>{current.flag}</span>
        <span className="font-medium">{current.label}</span>
      </button>
      {open && (
        <div className={`absolute z-50 bottom-full mb-2 left-2 right-2 rounded-md shadow-xl border overflow-hidden ${
          compact
            ? "bg-slate-900 border-white/10 text-white"
            : "bg-white dark:bg-slate-900 border-[#E2E8F0] dark:border-slate-700"
        }`}>
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => pick(l.code)}
              data-testid={`lang-${l.code}`}
              className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition ${
                compact ? "hover:bg-white/10" : "hover:bg-slate-50 dark:hover:bg-slate-800"
              } ${l.code === current.code ? (compact ? "bg-white/5" : "bg-[#FFFBF0] dark:bg-amber-900/10") : ""}`}
            >
              <span className="text-base">{l.flag}</span>
              <span className="flex-1 font-medium">{l.label}</span>
              {l.code === current.code && <Check className="w-4 h-4 text-[#C9A356]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
